/**
 * LE DÉNICHEUR — Quiz olfactif
 * Port vanilla JS de la maquette React (quiz-olfactif.jsx) — même logique de
 * scoring, mêmes questions, même comportement. Adapté pour tourner comme
 * section de thème Shopify (pas de build, pas de dépendance React).
 *
 * Source des données produit : /assets/ld-quiz-catalog.json (généré depuis
 * le mapping xlsx — voir scripts/build_catalog.py et README.md).
 * Source des variantes réelles (id, prix, dispo, image) : l'API AJAX
 * publique du storefront, /products/{handle}.js — aucun jeton requis,
 * jamais l'Admin API côté client.
 */
(function () {
  "use strict";

  var FAMILIES = ["frais", "boise", "gourmand", "epice", "cuir", "floral"];

  var FAMILY_LABELS = {
    frais: "Frais / propre",
    boise: "Boisé",
    gourmand: "Sucré / gourmand",
    epice: "Épicé / chaud",
    cuir: "Cuir / animalique",
    floral: "Floral",
  };

  // Trame réfléchie : d'abord le contexte du visiteur (qui répond, pour qui,
  // quel genre) et les contraintes dures qui cadrent toute la suite (budget) ;
  // ensuite le profil olfactif proprement dit (occasion, familles,
  // performance) ; enfin les détails pratiques de test (format, rythme) qui
  // ne prennent leur sens qu'une fois le profil connu.
  var QUESTIONS = [
    {
      key: "level",
      label: "Tu t'y connais en parfum ?",
      help: "On adapte le ton des recommandations, pas les parfums eux-mêmes.",
      type: "single",
      options: [
        { value: "debutant", label: "Débutant — je découvre" },
        { value: "amateur", label: "Amateur — je m'y intéresse" },
        { value: "connaisseur", label: "Connaisseur — je maîtrise mon sujet" },
      ],
    },
    {
      key: "recipient",
      label: "C'est pour qui ?",
      help: "Un achat pour soi ou un cadeau, on ajuste le message.",
      type: "single",
      options: [
        { value: "moi", label: "Pour moi" },
        { value: "cadeau", label: "Un cadeau" },
      ],
    },
    {
      key: "gender",
      label: "Pour qui cherches-tu ?",
      help: "On filtre les propositions en conséquence.",
      type: "single",
      options: [
        { value: "homme", label: "Homme" },
        { value: "femme", label: "Femme" },
        { value: "mixte", label: "Peu importe / mixte" },
      ],
    },
    {
      key: "budget",
      label: "Ton budget par décant ?",
      help: "Tous formats confondus — on affine le format juste après. Laisse au maximum pour ne rien filtrer.",
      type: "budget",
    },
    {
      key: "occasion",
      label: "Pour quelle occasion ?",
      help: "Oriente l'intensité et le style.",
      type: "single",
      options: [
        { value: "quotidien", label: "Quotidien / bureau", boost: { frais: 2, boise: 1 } },
        { value: "soir", label: "Soirée / sortie", boost: { epice: 2, gourmand: 1, cuir: 1 } },
        { value: "seduction", label: "Séduction", boost: { gourmand: 2, epice: 2, cuir: 1 } },
        { value: "signature", label: "Une signature qui me distingue", boost: { cuir: 2, boise: 2, epice: 1 } },
      ],
    },
    {
      key: "family",
      label: "Quelles ambiances t'attirent ?",
      help: "3 ambiances maximum — on proposera des décants pour chacune.",
      type: "multi",
      max: 3,
      options: [
        { value: "frais", label: "Frais / propre", boost: { frais: 3 } },
        { value: "boise", label: "Boisé", boost: { boise: 3 } },
        { value: "gourmand", label: "Sucré / gourmand", boost: { gourmand: 3 } },
        { value: "epice", label: "Épicé / chaud", boost: { epice: 3 } },
        { value: "cuir", label: "Cuir / animalique", boost: { cuir: 3 } },
        { value: "floral", label: "Floral", boost: { floral: 3 } },
      ],
    },
    {
      key: "performance",
      label: "Quelle performance recherches-tu ?",
      help: "Tenue et sillage.",
      type: "single",
      options: [
        { value: "legere", label: "Discrète, proche de peau" },
        { value: "moyenne", label: "Équilibrée" },
        { value: "forte", label: "Grosse tenue / bête de sillage" },
      ],
    },
    {
      key: "duration",
      label: "Comment veux-tu tester ?",
      help: "Détermine le format de décant conseillé.",
      type: "single",
      options: [
        { value: "court", label: "Plusieurs références vite fait (quelques jours)", formats: [3] },
        { value: "moyen", label: "Vivre avec sur le moyen terme", formats: [5, 10] },
        { value: "long", label: "En faire une signature sur le très long terme", formats: [30, 50] },
      ],
    },
    {
      key: "rate",
      label: "Tu vaporises combien de fois par jour ?",
      help: "On convertit le format en durée réelle selon ton usage.",
      type: "single",
      options: [
        { value: "standard", label: "Standard — quelques pschitts (3/jour)", perDay: 3 },
        { value: "genereux", label: "Généreux — bien couvert (5/jour)", perDay: 5 },
        { value: "nucleaire", label: "Nucléaire — on ne passe pas inaperçu (7/jour)", perDay: 7 },
      ],
    },
  ];

  // Base de conversion : 10 ml = 190 sprays → ~19 sprays/ml
  var SPRAYS_PER_ML = 19;
  function sprays(ml) { return Math.round(ml * SPRAYS_PER_ML); }
  function daysFor(ml, perDay) { return Math.round(sprays(ml) / perDay); }

  function findOption(key, value) {
    var q = QUESTIONS.filter(function (q) { return q.key === key; })[0];
    if (!q) return null;
    return q.options.filter(function (o) { return o.value === value; })[0] || null;
  }

  // Message d'intro des résultats : ajuste uniquement le TON (niveau de
  // connaissance, cadeau), ne touche jamais à la sélection de décants.
  function resultIntro(level, recipient) {
    var parts = [];
    if (recipient === "cadeau") {
      parts.push("Pour un cadeau, mise sur une valeur sûre : un format généreux fait toujours son effet.");
    }
    if (level === "debutant") {
      parts.push("Comme tu débutes, on a gardé des profils accessibles et faciles à porter — commence petit, tu affineras ensuite.");
    } else if (level === "connaisseur") {
      parts.push("Tu connais ton affaire — on va droit au but, sélection resserrée sur les jus qui valent le détour.");
    }
    return parts.join(" ");
  }

  // ---- SCORING (identique à computeResults() du .jsx) ----
  // enrichCache (optionnel) : handle -> données live /products/{handle}.js,
  // déjà peuplé en tâche de fond par prefetchAll() — sert au filtre budget.
  function computeResults(answers, catalog, enrichCache) {
    enrichCache = enrichCache || {};
    var gender = answers.gender;
    var perfWanted = answers.performance;
    var budgetMax = answers.budget != null ? answers.budget : null;

    var durationOpt = findOption("duration", answers.duration);
    var formats = (durationOpt && durationOpt.formats) || [];

    var rateOpt = findOption("rate", answers.rate);
    var perDay = (rateOpt && rateOpt.perDay) || 3;

    var formatInfo = formats.map(function (ml) {
      return { ml: ml, label: ml + " ml", sprays: sprays(ml), days: daysFor(ml, perDay) };
    });

    var selected = (Array.isArray(answers.family) && answers.family.length > 0)
      ? answers.family
      : FAMILIES;

    var occasionOpt = findOption("occasion", answers.occasion);
    var occBoost = (occasionOpt && occasionOpt.boost) || {};

    var genderPool = catalog.filter(function (p) {
      return gender === "mixte" || p.gender.indexOf(gender) !== -1 || p.gender.indexOf("mixte") !== -1;
    });
    var pool = genderPool;

    // Filtre dur budget : ne garde que les décants ayant au moins un des
    // formats conseillés (réponse "durée de test") dont le prix réel tient
    // dans le budget choisi. Comme pour le tag "Rupture", on ne pénalise
    // jamais un produit dont on n'a pas encore (ou plus) les données live —
    // par précaution, on ne cache rien sur un simple souci réseau/timing.
    if (budgetMax != null) {
      pool = genderPool.filter(function (p) {
        var live = enrichCache[p.handle];
        if (!live) return true;
        return formats.some(function (ml) {
          var price = priceForFormat(live, ml);
          return price != null && price <= budgetMax;
        });
      });
    }

    var order = ["legere", "moyenne", "forte"];
    var usedIds = {};

    // On affiche 3 décants par famille, tirés au hasard (pondéré par affinité)
    // parmi un vivier de 8 — pas toujours strictement les 2-3 mêmes en tête.
    // Deux décants à égalité de score ont autant de chances l'un que l'autre ;
    // un décant nettement mieux noté reste statistiquement favorisé sans être
    // garanti. Le vivier (8) est plus large que l'affichage (3) : la marge sert
    // aussi de filet si certains candidats sont tagués "Rupture" (resolveResults).
    var POOL_SIZE = 8;
    var DISPLAY_COUNT = 3;

    // Tirage aléatoire pondéré sans remise (algorithme Efraimidis-Spirakis) :
    // chaque item reçoit une clé random()^(1/poids) ; trier par clé décroissante
    // donne un ordre aléatoire où un poids plus fort a plus de chances d'arriver
    // devant, sans jamais l'imposer.
    function weightedShuffle(items, weightOf) {
      return items
        .map(function (it) { return { it: it, key: Math.pow(Math.random(), 1 / Math.max(weightOf(it), 0.0001)) }; })
        .sort(function (a, b) { return b.key - a.key; })
        .map(function (x) { return x.it; });
    }

    var groups = selected.map(function (fam) {
      var ranked = pool
        .map(function (p) {
          // Le bonus d'occasion ne s'applique qu'à LA famille affichée dans ce
          // groupe (ex. "gourmand"), jamais aux autres familles du produit —
          // sinon un décant très épicé/cuiré grimpe dans le classement
          // "gourmand" grâce à son épicé/cuir, pas grâce à son côté gourmand.
          var affinity = (p.scores[fam] || 0) * (10 + (occBoost[fam] || 0));
          if (perfWanted && p.performance === perfWanted) affinity += 6;
          else if (perfWanted && Math.abs(order.indexOf(perfWanted) - order.indexOf(p.performance)) === 1) affinity += 2;
          var copy = {};
          for (var k in p) copy[k] = p[k];
          copy.affinity = affinity;
          return copy;
        })
        .filter(function (p) { return p.scores[fam] > 0 && !usedIds[p.handle]; })
        .sort(function (a, b) { return b.affinity - a.affinity; })
        .slice(0, POOL_SIZE);

      var candidates = weightedShuffle(ranked, function (p) { return p.affinity; });

      candidates.forEach(function (p) { usedIds[p.handle] = true; });
      // "picks" = affichage optimiste immédiat, corrigé dès que resolveResults()
      // connaît la disponibilité réelle de chacun.
      return { family: fam, label: FAMILY_LABELS[fam], candidates: candidates, picks: candidates.slice(0, DISPLAY_COUNT), desiredFormats: formats };
    });

    // "Coups de cœur" hors budget : 2 décants qui correspondent au(x) profil(s)
    // choisi(s) mais qu'on a écartés du budget filter — pour donner envie de
    // pousser un peu, jamais pour remplacer un pick dans les groupes ci-dessus.
    // N'existe que si un budget restrictif a effectivement exclu des candidats.
    var BONUS_POOL_SIZE = 6;
    var BONUS_DISPLAY_COUNT = 2;
    // On veut un vrai "coup de cœur qui pousse le budget", pas un dépassement
    // de quelques euros à peine perceptible : on exige un écart minimum (le
    // plus grand des deux : 30% du budget, ou 8 €) avant de considérer un
    // candidat. Filet de sécurité : si rien n'atteint ce seuil (budget déjà
    // très haut, catalogue restreint), on retombe sur tous les candidats
    // exclus plutôt que de vider la section.
    var MIN_STRETCH_RATIO = 0.3;
    var MIN_STRETCH_ABS = 8;
    var bonus = { candidates: [], picks: [], desiredFormats: formats };
    if (budgetMax != null) {
      var affordableHandles = {};
      pool.forEach(function (p) { affordableHandles[p.handle] = true; });

      var bonusEligible = genderPool
        .filter(function (p) { return !affordableHandles[p.handle] && selected.some(function (fam) { return (p.scores[fam] || 0) > 0; }); })
        .map(function (p) {
          var live = enrichCache[p.handle];
          var pricesForDesired = formats
            .map(function (ml) { return priceForFormat(live, ml); })
            .filter(function (price) { return price != null; });
          var minPrice = pricesForDesired.length ? Math.min.apply(null, pricesForDesired) : null;

          var bestFamily = null, bestAffinity = -1;
          selected.forEach(function (fam) {
            var aff = (p.scores[fam] || 0) * (10 + (occBoost[fam] || 0));
            if (aff > bestAffinity) { bestAffinity = aff; bestFamily = fam; }
          });
          if (perfWanted && p.performance === perfWanted) bestAffinity += 6;
          else if (perfWanted && Math.abs(order.indexOf(perfWanted) - order.indexOf(p.performance)) === 1) bestAffinity += 2;
          var copy = {};
          for (var k in p) copy[k] = p[k];
          copy.affinity = bestAffinity;
          copy.bestFamily = bestFamily;
          copy.priceDelta = minPrice != null ? minPrice - budgetMax : null;
          return copy;
        })
        .filter(function (p) { return p.priceDelta != null; }); // pas de prix connu pour ces formats -> pas de badge fiable, on l'écarte du vivier

      var minStretch = Math.max(MIN_STRETCH_ABS, budgetMax * MIN_STRETCH_RATIO);
      var stretched = bonusEligible.filter(function (p) { return p.priceDelta >= minStretch; });
      var bonusPoolSource = stretched.length ? stretched : bonusEligible;

      var bonusRanked = bonusPoolSource
        .sort(function (a, b) { return b.affinity - a.affinity; })
        .slice(0, BONUS_POOL_SIZE);

      var bonusCandidates = weightedShuffle(bonusRanked, function (p) { return p.affinity; });
      bonus = { candidates: bonusCandidates, picks: bonusCandidates.slice(0, BONUS_DISPLAY_COUNT), desiredFormats: formats };
    }

    return { groups: groups, bonus: bonus, formatInfo: formatInfo, rateLabel: (rateOpt && rateOpt.label) || "" };
  }

  // ---- Résolution des vraies variantes Shopify (formats/prix/dispo) ----
  // Titres pas toujours normalisés ("3 ml" / "3 mL" / "3mL") → parsing tolérant.
  function parseMlFromTitle(title) {
    var m = /(\d+(?:[.,]\d+)?)\s*m\s*l/i.exec(String(title || ""));
    if (!m) return null;
    return parseFloat(m[1].replace(",", "."));
  }

  // Prix réel (€) d'un produit live pour un format ml donné, parmi les
  // variantes réellement en vente — null si ce format n'existe pas/plus.
  function priceForFormat(liveProduct, ml) {
    if (!liveProduct || !Array.isArray(liveProduct.variants)) return null;
    var match = null;
    liveProduct.variants.forEach(function (v) {
      if (!v.available) return;
      if (parseMlFromTitle(v.title) === ml && (match === null || v.price < match.price)) match = v;
    });
    return match ? match.price / 100 : null;
  }

  // Choisit, parmi les variantes réellement en vente, celle dont le format
  // se rapproche le plus des formats souhaités par la réponse "durée de test".
  // budgetMax (optionnel) : ne considère que les variantes dans le budget —
  // sauf si ça viderait la liste (garde-fou ; computeResults a déjà écarté
  // en amont les produits sans aucune option abordable).
  function pickBestVariant(variants, desiredFormats, budgetMax) {
    var parsed = variants
      .map(function (v) { return { variant: v, ml: parseMlFromTitle(v.title) }; })
      .filter(function (x) { return x.ml !== null; });
    if (!parsed.length) return null;

    if (budgetMax != null) {
      var affordable = parsed.filter(function (x) { return (x.variant.price / 100) <= budgetMax; });
      if (affordable.length) parsed = affordable;
    }

    if (!desiredFormats || !desiredFormats.length) return parsed[0].variant;

    var exact = parsed.filter(function (x) { return desiredFormats.indexOf(x.ml) !== -1; });
    if (exact.length) return exact[0].variant;

    var best = null, bestDist = Infinity;
    parsed.forEach(function (x) {
      var dist = Math.min.apply(null, desiredFormats.map(function (d) { return Math.abs(d - x.ml); }));
      if (dist < bestDist) { bestDist = dist; best = x.variant; }
    });
    return best;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function stripHtml(html, maxLen) {
    var text = String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (maxLen && text.length > maxLen) text = text.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
    return text;
  }

  // ---- Composant ----
  function LdQuiz(root) {
    this.root = root;
    this.catalogUrl = root.getAttribute("data-catalog-url");
    this.step = 0;
    this.answers = {};
    this.email = "";
    this.emailSent = false;
    this.emailError = "";
    this.catalog = null;
    this.loadError = false;
    this.enrichCache = {}; // handle -> product JSON (/products/{handle}.js)

    this.onRootClick = this.onRootClick.bind(this);
    this.onRootInput = this.onRootInput.bind(this);
    root.addEventListener("click", this.onRootClick);
    root.addEventListener("input", this.onRootInput);

    this.renderLoading();
    this.loadCatalog();
  }

  LdQuiz.prototype.loadCatalog = function () {
    var self = this;
    fetch(this.catalogUrl, { credentials: "omit" })
      .then(function (r) { if (!r.ok) throw new Error("catalog http " + r.status); return r.json(); })
      .then(function (data) { self.catalog = data; self.render(); self.prefetchAll(); })
      .catch(function () { self.loadError = true; self.render(); });
  };

  // Récupère en tâche de fond les données live (prix, dispo, tags) de tout
  // le catalogue dès que le quiz démarre — pas seulement des résultats
  // finaux. Sert à donner des bornes de prix réelles à la question budget
  // (question 9) sans attendre un aller-retour réseau à ce moment précis :
  // le temps que le visiteur réponde aux 8 questions précédentes, l'essentiel
  // est déjà en cache.
  LdQuiz.prototype.prefetchAll = function () {
    var self = this;
    this.catalog.forEach(function (p) {
      if (self.enrichCache[p.handle] !== undefined) return;
      fetch("/products/" + p.handle + ".js", { credentials: "omit" })
        .then(function (r) { if (!r.ok) throw new Error("product http " + r.status); return r.json(); })
        .then(function (data) { self.enrichCache[p.handle] = data; self.maybeRefreshBudgetStep(); })
        .catch(function () { self.enrichCache[p.handle] = false; });
    });
  };

  // Si le visiteur est justement sur la question budget quand assez de prix
  // deviennent disponibles, on rafraîchit une fois pour lui donner de vraies
  // bornes plutôt que la fourchette par défaut — sans re-render répété à
  // chaque produit qui arrive (renderBudgetQuestion arme déjà le "déjà prêt").
  LdQuiz.prototype.maybeRefreshBudgetStep = function () {
    if (this.isResult() || this._budgetRenderedReady) return;
    var q = this.currentQuestion();
    if (!q || q.type !== "budget") return;
    if (this.budgetBounds().ready) this.render();
  };

  // Bornes min/max réelles (€) du curseur budget, calées sur le(s) format(s)
  // conseillé(s) par la réponse "durée de test" et le pool genre — à partir
  // des prix live déjà en cache (prefetchAll). ready=false tant qu'aucun prix
  // n'est encore connu (fourchette par défaut affichée en attendant).
  LdQuiz.prototype.budgetBounds = function () {
    var self = this;
    var gender = this.answers.gender;
    var pool = this.catalog.filter(function (p) {
      return gender === "mixte" || p.gender.indexOf(gender) !== -1 || p.gender.indexOf("mixte") !== -1;
    });
    // Le budget est posé avant la question "durée de test" (la trame va du
    // contexte/contraintes vers le profil olfactif, puis les détails
    // pratiques) : on ne connaît donc pas encore le(s) format(s) conseillé(s)
    // ici. Les bornes portent sur TOUS les formats en vente, tous formats
    // confondus — le filtre budget final, lui, reste bien scopé au format
    // recommandé une fois la question "durée" répondue (voir computeResults).
    var prices = [];
    pool.forEach(function (p) {
      var live = self.enrichCache[p.handle];
      if (!live || !Array.isArray(live.variants)) return;
      live.variants.forEach(function (v) {
        if (v.available) prices.push(v.price / 100);
      });
    });
    if (!prices.length) return { min: 0, max: 300, ready: false };
    return { min: Math.max(0, Math.floor(Math.min.apply(null, prices))), max: Math.ceil(Math.max.apply(null, prices)), ready: true };
  };

  LdQuiz.prototype.renderLoading = function () {
    this.root.innerHTML = '<div class="ldq-loading">Chargement du quiz…</div>';
  };

  LdQuiz.prototype.currentQuestion = function () {
    return QUESTIONS[this.step];
  };

  LdQuiz.prototype.isResult = function () {
    return this.step >= QUESTIONS.length;
  };

  LdQuiz.prototype.choose = function (key, value) {
    this.answers[key] = value;
    var self = this;
    window.setTimeout(function () {
      self.step += 1;
      self.render();
      if (self.isResult()) self.resolveResults();
    }, 180);
  };

  LdQuiz.prototype.toggle = function (q, value) {
    var cur = Array.isArray(this.answers[q.key]) ? this.answers[q.key].slice() : [];
    var idx = cur.indexOf(value);
    if (idx !== -1) {
      cur.splice(idx, 1);
    } else {
      if (q.max && cur.length >= q.max) return;
      cur.push(value);
    }
    this.answers[q.key] = cur;
    this.render();
  };

  LdQuiz.prototype.restart = function () {
    this.step = 0;
    this.answers = {};
    this.email = "";
    this.emailSent = false;
    this.emailError = "";
    this._results = null;
    this.render();
  };

  LdQuiz.prototype.onRootClick = function (e) {
    var el = e.target.closest("[data-ldq-action]");
    if (!el || !this.root.contains(el)) return;
    var action = el.getAttribute("data-ldq-action");
    var q = this.currentQuestion();

    if (action === "choose-single") {
      this.choose(el.getAttribute("data-key"), el.getAttribute("data-value"));
    } else if (action === "toggle-multi") {
      this.toggle(q, el.getAttribute("data-value"));
    } else if (action === "continue-multi") {
      if (Array.isArray(this.answers[q.key]) && this.answers[q.key].length > 0) {
        this.step += 1;
        this.render();
        if (this.isResult()) this.resolveResults();
      }
    } else if (action === "continue-budget") {
      if (this.answers.budget == null) this.answers.budget = this.budgetBounds().max;
      this.step += 1;
      this.render();
      if (this.isResult()) this.resolveResults();
    } else if (action === "back") {
      if (this.step > 0) { this.step -= 1; this.render(); }
    } else if (action === "restart") {
      this.restart();
    } else if (action === "email-submit") {
      this.submitEmail();
    }
  };

  LdQuiz.prototype.onRootInput = function (e) {
    if (e.target.matches("[data-ldq-email-input]")) {
      this.email = e.target.value;
    } else if (e.target.matches("[data-ldq-budget-input]")) {
      // Mise à jour directe du DOM (pas de render() complet) pour ne pas
      // interrompre le glissé du curseur — même logique que l'email plus haut.
      this.answers.budget = Number(e.target.value);
      var label = this.root.querySelector("[data-ldq-budget-label]");
      if (label) label.textContent = this.answers.budget + " €";
    }
  };

  // Tag Shopify que le vendeur pose lui-même sur un décant réellement en
  // rupture (le stock affiché par l'API Shopify n'est pas fiable sur cette
  // boutique — voir README.md). Un décant portant ce tag est écarté des
  // résultats du quiz, au profit du candidat suivant dans sa famille.
  var OUT_OF_STOCK_TAG = "rupture";

  function isTaggedOutOfStock(liveProduct) {
    if (!liveProduct || !Array.isArray(liveProduct.tags)) return false;
    return liveProduct.tags.some(function (t) {
      return String(t).trim().toLowerCase() === OUT_OF_STOCK_TAG;
    });
  }

  // Récupère les variantes réelles (id, prix, dispo, tags) pour les handles
  // des résultats via l'API AJAX publique du storefront — aucun jeton requis.
  LdQuiz.prototype.resolveResults = function () {
    var self = this;
    var results = computeResults(this.answers, this.catalog, this.enrichCache);
    this._results = results;
    this.render(); // affiche déjà les 2 meilleurs candidats le temps du fetch (affichage optimiste)

    var handles = [];
    results.groups.forEach(function (g) {
      g.candidates.forEach(function (p) {
        if (handles.indexOf(p.handle) === -1) handles.push(p.handle);
      });
    });
    results.bonus.candidates.forEach(function (p) {
      if (handles.indexOf(p.handle) === -1) handles.push(p.handle);
    });

    Promise.all(handles.map(function (handle) {
      if (self.enrichCache[handle] !== undefined) return Promise.resolve();
      return fetch("/products/" + handle + ".js", { credentials: "omit" })
        .then(function (r) { if (!r.ok) throw new Error("product http " + r.status); return r.json(); })
        .then(function (data) { self.enrichCache[handle] = data; })
        .catch(function () { self.enrichCache[handle] = false; }); // fiche introuvable/dépubliée
    })).then(function () {
      // Une fois les tags connus : ne garder par groupe que les 3 premiers
      // candidats (déjà mélangés aléatoirement dans computeResults) qui ne
      // portent PAS le tag "Rupture". Un échec de fetch (enrichCache === false)
      // n'exclut jamais un produit — seul un tag positif le fait, pour ne rien
      // cacher sur un simple souci réseau.
      results.groups.forEach(function (g) {
        g.picks = g.candidates
          .filter(function (p) { return !isTaggedOutOfStock(self.enrichCache[p.handle]); })
          .slice(0, 3);
      });
      results.bonus.picks = results.bonus.candidates
        .filter(function (p) { return !isTaggedOutOfStock(self.enrichCache[p.handle]); })
        .slice(0, 2);
      self.render();
    });
  };

  LdQuiz.prototype.submitEmail = function () {
    var self = this;
    if (!this.email || this.email.indexOf("@") === -1) {
      this.emailError = "Adresse email invalide.";
      this.render();
      return;
    }
    this.emailError = "";
    // Formulaire natif "newsletter" Shopify : ajoute/actualise le client,
    // l'inscrit à la newsletter et le tague "quiz" pour segmentation dans
    // Shopify Email (voir README.md — limites de l'automatisation sur le
    // plan Basic).
    var body = new URLSearchParams();
    body.set("form_type", "customer");
    body.set("utf8", "✓");
    body.set("contact[email]", this.email);
    body.set("contact[tags]", "newsletter,quiz");

    fetch("/contact#newsletter", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })
      .then(function () { self.emailSent = true; self.render(); })
      .catch(function () {
        self.emailError = "L'envoi a échoué — réessaie dans un instant.";
        self.render();
      });
  };

  // ---- Rendu ----
  LdQuiz.prototype.render = function () {
    if (this.loadError) {
      this.root.innerHTML = '<p class="ldq-error">Le quiz est momentanément indisponible. Réessaie un peu plus tard, ou <a href="/collections/decants">explore nos décants</a> directement.</p>';
      return;
    }
    if (!this.catalog) { this.renderLoading(); return; }

    var html = '<div class="ldq">';
    if (!this.isResult()) {
      html += this.renderProgress() + this.renderQuestion();
    } else {
      html += this.renderResults();
    }
    html += "</div>";
    this.root.innerHTML = html;
  };

  LdQuiz.prototype.renderProgress = function () {
    var progress = Math.round((Math.min(this.step, QUESTIONS.length) / QUESTIONS.length) * 100);
    return (
      '<div class="ldq-progress">' +
        '<div class="ldq-progress__track"><div class="ldq-progress__fill" style="width:' + progress + '%"></div></div>' +
        '<div class="ldq-progress__label">Question ' + (this.step + 1) + " / " + QUESTIONS.length + "</div>" +
      "</div>"
    );
  };

  LdQuiz.prototype.renderBack = function () {
    return this.step > 0
      ? '<button type="button" class="ldq-back" data-ldq-action="back">← Précédent</button>'
      : "";
  };

  LdQuiz.prototype.renderBudgetQuestion = function (q) {
    var bounds = this.budgetBounds();
    this._budgetRenderedReady = bounds.ready;
    if (this.answers.budget == null || this.answers.budget > bounds.max || this.answers.budget < bounds.min) {
      this.answers.budget = bounds.max;
    }
    var current = this.answers.budget;
    var loadingNote = bounds.ready ? "" : '<p class="ldq-budget-loading">Calcul des prix réels en cours — la fourchette ci-dessous s\'affinera dans un instant.</p>';

    return (
      '<div class="ldq-question">' +
        "<h2>" + escapeHtml(q.label) + "</h2>" +
        '<p class="ldq-help">' + escapeHtml(q.help) + "</p>" +
        '<div class="ldq-budget">' +
          '<div class="ldq-budget__row"><span>Jusqu’à</span><span class="ldq-budget__value" data-ldq-budget-label>' + current + " €</span></div>" +
          '<input type="range" class="ldq-budget__range" data-ldq-budget-input min="' + bounds.min + '" max="' + bounds.max + '" step="1" value="' + current + '">' +
          '<div class="ldq-budget__bounds"><span>' + bounds.min + " €</span><span>" + bounds.max + " €</span></div>" +
          loadingNote +
        "</div>" +
        '<button type="button" class="ldq-continue" data-ldq-action="continue-budget">Voir mes résultats</button>' +
        this.renderBack() +
      "</div>"
    );
  };

  LdQuiz.prototype.renderQuestion = function () {
    var q = this.currentQuestion();
    if (q.type === "budget") return this.renderBudgetQuestion(q);
    var isMulti = q.type === "multi";
    var curArr = Array.isArray(this.answers[q.key]) ? this.answers[q.key] : [];

    var optionsHtml = q.options.map(function (opt) {
      var selected = isMulti ? curArr.indexOf(opt.value) !== -1 : this.answers[q.key] === opt.value;
      var atMax = isMulti && q.max && curArr.length >= q.max;
      var locked = atMax && !selected;
      var action = isMulti ? "toggle-multi" : "choose-single";
      return (
        '<button type="button" class="ldq-option' + (selected ? " is-selected" : "") + (locked ? " is-locked" : "") + '"' +
          ' data-ldq-action="' + action + '" data-key="' + escapeHtml(q.key) + '" data-value="' + escapeHtml(opt.value) + '"' +
          (locked ? " disabled" : "") + ">" +
          (isMulti ? '<span class="ldq-check">' + (selected ? "✓" : "") + "</span>" : "") +
          escapeHtml(opt.label) +
        "</button>"
      );
    }, this).join("");

    var footer = "";
    if (isMulti) {
      var n = curArr.length;
      footer =
        (q.max ? '<div class="ldq-counter">' + n + " / " + q.max + " sélectionnée" + (n > 1 ? "s" : "") + "</div>" : "") +
        '<button type="button" class="ldq-continue" data-ldq-action="continue-multi"' + (n === 0 ? " disabled" : "") + ">Continuer</button>";
    }

    return (
      '<div class="ldq-question">' +
        "<h2>" + escapeHtml(q.label) + "</h2>" +
        '<p class="ldq-help">' + escapeHtml(q.help) + "</p>" +
        '<div class="ldq-options">' + optionsHtml + "</div>" +
        footer + this.renderBack() +
      "</div>"
    );
  };

  LdQuiz.prototype.renderResults = function () {
    var self = this;
    var results = this._results || computeResults(this.answers, this.catalog, this.enrichCache);
    this._results = results;

    var intro = resultIntro(this.answers.level, this.answers.recipient);

    var formatCards = results.formatInfo.map(function (f) {
      return (
        '<div class="ldq-format-card">' +
          '<div class="ldq-format-card__ml">' + f.label + "</div>" +
          '<div class="ldq-format-card__meta">~' + f.sprays + " sprays · ~" + f.days + " jours</div>" +
        "</div>"
      );
    }).join("");

    var groupsHtml = results.groups.map(function (g) {
      var picksHtml;
      if (!g.picks.length) {
        picksHtml = '<p class="ldq-empty">Rien dans cette famille pour ces critères — dis-nous, on déniche.</p>';
      } else {
        picksHtml = '<div class="ldq-picks">' + g.picks.map(function (p) {
          return self.renderProductCard(p, g.desiredFormats);
        }).join("") + "</div>";
      }
      return (
        '<section class="ldq-group">' +
          '<div class="ldq-group__head"><h3>' + escapeHtml(g.label) + '</h3><span>' + g.picks.length + " décant" + (g.picks.length > 1 ? "s" : "") + "</span></div>" +
          picksHtml +
        "</section>"
      );
    }).join("");

    var bonusHtml = "";
    if (results.bonus.picks.length) {
      var budgetMax = this.answers.budget;
      bonusHtml =
        '<section class="ldq-bonus">' +
          '<div class="ldq-bonus__head"><h3>Pour pousser un peu…</h3><p>Deux coups de cœur qui sortent de ton budget — histoire de voir ce que ça donnerait en montant un peu.</p></div>' +
          '<div class="ldq-picks">' + results.bonus.picks.map(function (p) {
            return self.renderProductCard(p, results.bonus.desiredFormats, {
              ignoreBudget: true,
              note: p.bestFamily ? "Fort en " + FAMILY_LABELS[p.bestFamily].toLowerCase() : "",
              badge: function (variant) {
                var over = variant.price / 100 - budgetMax;
                return over > 0 ? '<span class="ldq-card__over">+' + over.toFixed(2).replace(".", ",") + " € vs ton budget</span>" : "";
              },
            });
          }).join("") + "</div>" +
        "</section>";
    }

    var emailBlock;
    if (this.emailSent) {
      emailBlock = '<p class="ldq-email-done">C\'est noté — à bientôt sur Le Dénicheur.</p>';
    } else {
      emailBlock =
        '<p>Tu veux recevoir cette sélection par email (et quelques conseils de dénicheur) ?</p>' +
        '<div class="ldq-email-row">' +
          '<input type="email" placeholder="ton@email.fr" data-ldq-email-input value="' + escapeHtml(this.email) + '">' +
          '<button type="button" data-ldq-action="email-submit">Recevoir</button>' +
        "</div>" +
        (this.emailError ? '<div class="ldq-email-error">' + escapeHtml(this.emailError) + "</div>" : "");
    }

    return (
      '<div class="ldq-results">' +
        "<h2>Voici ta sélection</h2>" +
        (intro ? '<p class="ldq-intro">' + escapeHtml(intro) + "</p>" : "") +
        '<p class="ldq-help">' + (results.formatInfo.length > 1 ? "Formats conseillés pour ce que tu recherches :" : "Format conseillé pour ce que tu recherches :") + "</p>" +
        '<div class="ldq-formats">' + formatCards + "</div>" +
        '<p class="ldq-note">Durées estimées à partir de ton rythme' + (results.rateLabel ? " (" + escapeHtml(results.rateLabel.split(" —")[0].toLowerCase()) + ")" : "") + " : chaque format contient un nombre de sprays donné, qu'on divise par ton usage quotidien.</p>" +
        '<div class="ldq-groups">' + groupsHtml + "</div>" +
        bonusHtml +
        '<div class="ldq-email">' + emailBlock + "</div>" +
        '<button type="button" class="ldq-restart" data-ldq-action="restart">↺ Recommencer le quiz</button>' +
      "</div>"
    );
  };

  // opts (optionnel) : { ignoreBudget: bool — ignore le budget pour le choix
  // de variante (carte "hors budget"), note: texte affiché sous la marque,
  // badge: function(variant) -> html, affiché à côté du prix }.
  LdQuiz.prototype.renderProductCard = function (p, desiredFormats, opts) {
    opts = opts || {};
    var live = this.enrichCache[p.handle];
    var title = p.name, vendor = p.brand, image = null, excerpt = "", href = "/products/" + p.handle, priceHtml = "";

    if (live === undefined) {
      // fetch encore en cours : carte "squelette" avec les données du catalogue
      return (
        '<a class="ldq-card ldq-card--loading" href="' + escapeHtml(href) + '">' +
          '<div class="ldq-card__body">' +
            '<div class="ldq-card__title">' + escapeHtml(title) + "</div>" +
            '<div class="ldq-card__vendor">' + escapeHtml(vendor) + "</div>" +
          "</div>" +
        "</a>"
      );
    }

    if (live) {
      title = live.title || title;
      vendor = live.vendor || vendor;
      image = live.featured_image || (live.images && live.images[0]) || null;
      excerpt = stripHtml(live.description, 110);
      var budgetForVariant = opts.ignoreBudget ? null : (this.answers.budget != null ? this.answers.budget : null);
      var variant = pickBestVariant(live.variants || [], desiredFormats, budgetForVariant);
      if (variant) {
        href = "/products/" + p.handle + "?variant=" + variant.id;
        priceHtml = '<span class="ldq-card__price">' + (variant.price / 100).toFixed(2).replace(".", ",") + " € · " + variant.title + "</span>" +
          (opts.badge ? opts.badge(variant) : "");
      }
    }

    return (
      '<a class="ldq-card' + (opts.ignoreBudget ? " ldq-card--bonus" : "") + '" href="' + escapeHtml(href) + '">' +
        (image ? '<img class="ldq-card__img" src="' + escapeHtml(image) + '" alt="" loading="lazy">' : "") +
        '<div class="ldq-card__body">' +
          '<div class="ldq-card__title">' + escapeHtml(title) + "</div>" +
          '<div class="ldq-card__vendor">' + escapeHtml(vendor) + (excerpt ? " — " + escapeHtml(excerpt) : "") + "</div>" +
          (opts.note ? '<div class="ldq-card__note">' + escapeHtml(opts.note) + "</div>" : "") +
          '<div class="ldq-card__footer">' + priceHtml + '<span class="ldq-card__cta">Voir la fiche →</span></div>' +
        "</div>" +
      "</a>"
    );
  };

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-ldq-root]").forEach(function (el) {
      new LdQuiz(el);
    });
  });
})();
